<template>
  <div class="customer_service_wrap">
    <!-- 客服图标按钮 -->
    <div
      class="service_btn"
      :class="{ 'has_unread': totalUnread > 0 }"
      @click="toggleChat"
    >
      <i class="el-icon-headset"></i>
      <span v-if="totalUnread > 0" class="unread_dot">
        {{ totalUnread > 99 ? '99+' : totalUnread }}
      </span>
    </div>

    <!-- 聊天框 -->
    <transition name="chat-slide">
      <div v-show="showChat" class="chat_box" ref="chatBox" :style="chatBoxStyle">
        <!-- 最左侧导航栏 -->
        <div class="nav_bar">
          <div
            class="nav_item"
            :class="{ active: activeTab === 'chat' }"
            @click="switchTab('chat')"
            title="最近聊天"
          >
            <i class="el-icon-chat-dot-round"></i>
            <span v-if="totalUnread > 0" class="nav_badge">{{ totalUnread > 99 ? '99+' : totalUnread }}</span>
          </div>
          <div
            class="nav_item"
            :class="{ active: activeTab === 'contacts' }"
            @click="switchTab('contacts')"
            title="成员列表"
          >
            <i class="el-icon-user"></i>
          </div>
          <div
            class="nav_item"
            :class="{ active: activeTab === 'files' }"
            @click="switchTab('files')"
            title="文件记录"
          >
            <i class="el-icon-folder-opened"></i>
          </div>
        </div>

        <!-- 中间列表区域 -->
        <div class="chat_sidebar">
          <!-- 最近聊天 -->
          <template v-if="activeTab === 'chat'">
            <div class="sidebar_header">
              <span class="sidebar_title">最近聊天</span>
            </div>
            <div class="conversation_list">
              <div
                v-for="room in recentChatList"
                :key="room.id"
                class="conversation_item"
                :class="{ active: currentRoom && currentRoom.id === room.id }"
                @click="selectRoom(room)"
              >
                <div class="conv_avatar" :class="{ 'group_avatar': room.roomType === 2 }">
                  <template v-if="room.roomType === 2">
                    <i class="el-icon-user"></i>
                  </template>
                  <template v-else>
                    <span class="avatar_text">{{ room.roomName ? room.roomName.charAt(0) : '?' }}</span>
                  </template>
                  <span v-if="room.unReadNum > 0" class="conv_unread">
                    {{ room.unReadNum > 99 ? '99+' : room.unReadNum }}
                  </span>
                </div>
                <div class="conv_info">
                  <div class="conv_top">
                    <span class="conv_name">{{ room.roomName }}</span>
                    <span class="conv_time">{{ formatTime(room.updateTime) }}</span>
                  </div>
                  <div class="conv_bottom">
                    <span class="conv_msg">暂无消息</span>
                  </div>
                </div>
              </div>
              <div v-if="recentChatList.length === 0" class="empty_list">
                <p>暂无聊天记录</p>
              </div>
            </div>
          </template>

          <!-- 成员列表（老师列表） -->
          <template v-else-if="activeTab === 'contacts'">
            <div class="sidebar_header">
              <span class="sidebar_title">老师列表</span>
            </div>
            <div class="conversation_list">
              <div
                v-for="teacher in teacherList"
                :key="teacher.userId"
                class="conversation_item"
                @click="selectTeacher(teacher)"
              >
                <div class="conv_avatar">
                  <img v-if="teacher.avatar" :src="teacher.avatar" alt="" />
                  <span v-else class="avatar_text">{{ teacher.nickName ? teacher.nickName.charAt(0) : '师' }}</span>
                </div>
                <div class="conv_info">
                  <div class="conv_top">
                    <span class="conv_name">{{ teacher.nickName }}</span>
                  </div>
                  <div class="conv_bottom">
                    <span class="conv_msg">{{teacher.courseName}}</span>
                  </div>
                </div>
              </div>
              <div v-if="teacherList.length === 0" class="empty_list">
                <p>暂无老师</p>
              </div>
            </div>
          </template>

          <!-- 文件记录 -->
          <template v-else-if="activeTab === 'files'">
            <div class="sidebar_header">
              <span class="sidebar_title">文件记录</span>
            </div>
            <div class="file_tabs">
              <div
                class="file_tab"
                :class="{ active: fileTab === 'images' }"
                @click="fileTab = 'images'"
              >
                <i class="el-icon-picture-outline"></i>
                <span>图片</span>
              </div>
              <div
                class="file_tab"
                :class="{ active: fileTab === 'files' }"
                @click="fileTab = 'files'"
              >
                <i class="el-icon-document"></i>
                <span>文件</span>
              </div>
            </div>
            <div class="file_list">
              <!-- 图片列表 -->
              <template v-if="fileTab === 'images'">
                <div class="image_grid" v-if="imageList.length > 0">
                  <div
                    v-for="(img, index) in imageList"
                    :key="index"
                    class="image_item"
                  >
                    <el-image
                      :src="img.content"
                      :preview-src-list="imageList.map(i => i.content)"
                      fit="cover"
                    />
                    <div class="image_info">{{ formatFullTime(img.timestamp) }}</div>
                  </div>
                </div>
                <div v-else class="empty_list">
                  <p>暂无图片</p>
                </div>
              </template>
              <!-- 文件列表 -->
              <template v-else>
                <div v-if="fileList.length > 0">
                  <div
                    v-for="(file, index) in fileList"
                    :key="index"
                    class="file_item"
                  >
                    <i class="el-icon-document"></i>
                    <div class="file_info">
                      <span class="file_name">{{ file.name }}</span>
                      <span class="file_date">{{ formatFullTime(file.timestamp) }}</span>
                    </div>
                  </div>
                </div>
                <div v-else class="empty_list">
                  <p>暂无文件</p>
                </div>
              </template>
            </div>
          </template>
        </div>

        <!-- 右侧聊天区域 -->
        <div class="chat_main">
          <!-- 文件记录展示 -->
          <template v-if="activeTab === 'files'">
            <div class="chat_header drag_handle" @mousedown="startDrag">
              <span class="chat_title">{{ fileTab === 'images' ? '图片' : '文件' }}</span>
              <div class="header_actions" @mousedown.stop>
                <i class="el-icon-minus" @click="toggleChat"></i>
                <i class="el-icon-close" @click="closeChat"></i>
              </div>
            </div>
            <div class="files_main_content">
              <div class="files_display_area">
                <!-- 图片展示 -->
                <template v-if="fileTab === 'images'">
                  <div class="images_gallery" v-if="imageList.length > 0">
                    <div
                      v-for="(img, index) in imageList"
                      :key="index"
                      class="gallery_item"
                    >
                      <el-image
                        :src="img.content"
                        :preview-src-list="imageList.map(i => i.content)"
                        fit="cover"
                      />
                      <div class="gallery_item_info">
                        <span class="sender">{{ img.senderName }}</span>
                        <span class="time">{{ formatFullTime(img.timestamp) }}</span>
                      </div>
                    </div>
                  </div>
                  <div v-else class="files_empty">
                    <i class="el-icon-picture-outline"></i>
                    <p>暂无图片记录</p>
                  </div>
                </template>
                <!-- 文件展示 -->
                <template v-else>
                  <div class="files_list_main" v-if="fileList.length > 0">
                    <div
                      v-for="(file, index) in fileList"
                      :key="index"
                      class="file_list_item"
                    >
                      <i class="el-icon-document"></i>
                      <div class="file_detail">
                        <span class="file_name">{{ file.name || '文件' }}</span>
                        <div class="file_meta">
                          <span>{{ file.senderName }}</span>
                          <span>{{ formatFullTime(file.timestamp) }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div v-else class="files_empty">
                    <i class="el-icon-document"></i>
                    <p>暂无文件记录</p>
                  </div>
                </template>
              </div>
            </div>
          </template>
          <!-- 聊天区域 -->
          <template v-else-if="currentRoom">
            <!-- 聊天头部 -->
            <div class="chat_header drag_handle" @mousedown="startDrag">
              <span class="chat_title">{{ currentRoom.roomName }}</span>
              <span v-if="currentRoom.roomType === 2" class="member_count">
                ({{ currentRoomMembers.length }}人)
              </span>
              <div class="header_actions" @mousedown.stop>
                <i class="el-icon-minus" @click="toggleChat"></i>
                <i class="el-icon-close" @click="closeChat"></i>
              </div>
            </div>

            <!-- 消息列表 -->
            <div class="message_list" ref="messageList">
              <div
                v-for="(message, index) in currentMessages"
                :key="message.id"
                class="message_wrapper"
              >
                <!-- 时间分割线 -->
                <div v-if="showTimeDivider(message, index)" class="time_divider">
                  <span>{{ formatFullTime(message.createTime) }}</span>
                </div>

                <!-- 普通消息 -->
                <div
                  class="message_item"
                  :class="{ 'message_self': message.senderId == currentUserId }"
                >
                  <!-- 头像 -->
                  <div class="message_avatar">
                    <img v-if="message.senderAvatar" :src="message.senderAvatar" alt="" />
                    <span v-else class="avatar_text">{{ message.senderName ? message.senderName.charAt(0) : '?' }}</span>
                  </div>

                  <!-- 消息主体 -->
                  <div class="message_body">
                    <!-- 群聊显示发送者名称 -->
                    <div v-if="currentRoom.roomType === 2 && message.senderId != currentUserId" class="sender_name">
                      {{ message.senderName }}
                    </div>
                    <div class="message_bubble" :class="{ 'image_bubble': message.msgType === 2, 'file_bubble': message.msgType === 3 }">
                      <template v-if="message.msgType === 1">
                        <span class="message_text">{{ message.content }}</span>
                      </template>
                      <template v-else-if="message.msgType === 2">
                        <el-image
                          :src="message.content"
                          :preview-src-list="[message.content]"
                          fit="cover"
                          class="message_image"
                        />
                      </template>
                      <template v-else-if="message.msgType === 3">
                        <div class="message_file" @click="downloadFile(message)">
                          <i class="el-icon-document"></i>
                          <div class="file_info">
                            <span class="file_name">[文件] {{ message.content }}</span>
                          </div>
                        </div>
                      </template>
                      <template v-else>
                        <span class="message_text">{{ message.content }}</span>
                      </template>
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="currentMessages.length === 0" class="empty_message">
                <p>暂无消息，开始聊天吧</p>
              </div>
            </div>

            <!-- 输入区域 -->
            <div class="input_area">
              <div class="input_box">
                <div class="input_toolbar">
                  <el-upload
                    ref="imageUpload"
                    action=""
                    :auto-upload="false"
                    :show-file-list="false"
                    accept="image/*"
                    :on-change="handleImageSelect"
                  >
                    <i class="el-icon-picture-outline toolbar_icon" title="发送图片"></i>
                  </el-upload>
                </div>
                <div class="input_container">
                  <textarea
                    v-model="messageInput"
                    placeholder="请输入消息..."
                    @keydown.enter="handleEnterKey"
                  ></textarea>
                </div>
              </div>
            </div>
          </template>

          <!-- 未选择会话时的占位 -->
          <template v-else>
            <div class="chat_header empty_header drag_handle" @mousedown="startDrag">
              <div class="header_actions" @mousedown.stop>
                <i class="el-icon-minus" @click="toggleChat"></i>
                <i class="el-icon-close" @click="closeChat"></i>
              </div>
            </div>
            <div class="chat_placeholder">
              <i class="el-icon-chat-dot-round"></i>
              <p>选择一位老师开始咨询</p>
            </div>
          </template>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import to from "await-to-js";
import api from "@/fetch/api";
import chatApi from '@/fetch/url';
import {
  getUserRooms,
  getRoomMemberList,
  getUnReadMessages,
  readMessage,
  getTeacherList,
  getOrCreatePrivateRoom
} from "@/fetch/api/chat";
import { createWebSocket, destroyWebSocket } from '@/fetch/websocket'
import { setStore, getStore, getCookies, generateUniqueString } from '@/utils/utils'

// localStorage 存储的 key
const STORAGE_KEYS = {
  MESSAGES: 'chat_messages',
  ROOMS: 'chat_rooms'
}

export default {
  name: 'CustomerServiceChat',
  data() {
    return {
      chatToken: "",
      // WebSocket 实例
      ws: null,
      wsConnected: false,
      currentJoinedRoomId: null, // 当前已加入的房间ID

      // 聊天框显示状态
      showChat: false,

      // 当前激活的 tab: chat, contacts, files
      activeTab: 'chat',

      // 文件记录的子 tab: images, files
      fileTab: 'images',

      // 当前登录用户
      currentUserId: '',
      currentUserName: '',
      currentUserAvatar: '',
      currentUserType: 1, // 学生类型

      // 聊天室列表
      roomList: [],
      currentRoom: null,
      // 当前聊天室成员列表
      currentRoomMembers: [],

      // 老师列表（联系人）
      teacherList: [],

      // 消息相关 { roomId: [messages] }
      messages: {},
      messageInput: '',

      // 未读消息数
      totalUnread: 0,

      // 拖拽相关
      isDragging: false,
      hasDragged: false,
      dragStartX: 0,
      dragStartY: 0,
      chatBoxX: 0,
      chatBoxY: 0
    }
  },

  computed: {
    // 当前聊天室的消息列表
    currentMessages() {
      if (!this.currentRoom) return []
      return this.messages[this.currentRoom.id] || []
    },
    // 过滤后的聊天室列表（搜索用）
    filteredRoomList() {
      return this.roomList
    },
    // 最近聊天列表（有消息记录的聊天室）
    recentChatList() {
      return this.roomList.sort((a, b) => {
        const timeA = new Date(a.updateTime || 0).getTime()
        const timeB = new Date(b.updateTime || 0).getTime()
        return timeB - timeA
      })
    },
    // 所有图片列表
    imageList() {
      const images = []
      Object.values(this.messages).forEach(msgList => {
        if (Array.isArray(msgList)) {
          msgList.forEach(msg => {
            if (msg && msg.msgType === 2) {
              images.push(msg)
            }
          })
        }
      })
      return images.sort((a, b) => new Date(b.createTime) - new Date(a.createTime))
    },
    // 所有文件列表
    fileList() {
      const files = []
      Object.values(this.messages).forEach(msgList => {
        if (Array.isArray(msgList)) {
          msgList.forEach(msg => {
            if (msg && msg.msgType === 3) {
              files.push(msg)
            }
          })
        }
      })
      return files.sort((a, b) => new Date(b.createTime) - new Date(a.createTime))
    },
    // 聊天框位置样式
    chatBoxStyle() {
      if (!this.hasDragged) {
        return {}
      }
      return {
        position: 'fixed',
        right: 'auto',
        bottom: 'auto',
        left: this.chatBoxX + 'px',
        top: this.chatBoxY + 'px'
      }
    }
  },

  created() {
    this.initCurrentUser()
    this.loadLocalData()
    this.chatTokenFn()
  },

  beforeDestroy() {
    // 保存数据到本地存储
    this.saveLocalData()
    // 离开当前房间
    if (this.currentJoinedRoomId) {
      this.leaveRoom(this.currentJoinedRoomId)
    }
    // 销毁 WebSocket
    destroyWebSocket()
    // 清理拖拽事件监听
    document.removeEventListener('mousemove', this.onDrag)
    document.removeEventListener('mouseup', this.stopDrag)
  },

  methods: {
    async chatTokenFn() {
      const [err, result] = await to(api.chatToken());
      if (err) return Promise.reject(err);
      if (result.code === 200) {
        this.chatToken = result.data || '';
        // 获取token成功后，加载房间列表和初始化WebSocket       
        this.loadTeacherList()
        this.initWebSocket()
      }
    },

    // 初始化当前用户信息
    initCurrentUser() {
      const userInfo = getCookies('userInfo')
      if (userInfo) {
        try {
          const info = typeof userInfo === 'string' ? JSON.parse(userInfo) : userInfo
          this.currentUserId = info.id || info.studentId || ''
          this.currentUserName = info.name || info.studentName || '学生'
          this.currentUserAvatar = info.avatar || ''
        } catch (e) {
          this.currentUserId = ''
          this.currentUserName = '学生'
        }
      }
    },

    // 加载本地存储的数据
    loadLocalData() {
      const messages = getStore(STORAGE_KEYS.MESSAGES)
      if (messages) {
        this.messages = messages
      }
      const rooms = getStore(STORAGE_KEYS.ROOMS)
      if (rooms) {
        this.roomList = rooms
        this.calculateTotalUnread()
      }
    },

    // 保存数据到本地存储
    saveLocalData() {
      setStore(STORAGE_KEYS.MESSAGES, this.messages)
      setStore(STORAGE_KEYS.ROOMS, this.roomList)
    },

    // 加载聊天室列表
    async loadRoomList() {
      const [err, result] = await to(getUserRooms({}, this.chatToken));
      if (err) {
        console.error('获取聊天室列表失败:', err)
        return
      }
      if (result.code === 200) {
        this.roomList = result.data || []
        this.calculateTotalUnread()
        this.saveLocalData()
      }
    },

    // 加载老师列表（联系人）
    async loadTeacherList() {
      const [err, result] = await to(getTeacherList({}, this.chatToken));
      if (err) {
        console.error('获取老师列表失败:', err)
        return
      }
      if (result.code === 200) {
        this.teacherList = result.data || []
      }
    },

    // 计算总未读数
    calculateTotalUnread() {
      this.totalUnread = this.roomList.reduce((sum, room) => sum + (room.unReadNum || 0), 0)
    },

    // 初始化 WebSocket 连接
    initWebSocket() {
      if (!this.chatToken) {
        console.error('chatToken 为空，无法连接 WebSocket')
        return
      }

      // 构建 WebSocket URL
      let wsUrl = chatApi.chatUrl || '';
      if (wsUrl.startsWith('//')) {
        // 处理以 // 开头的协议相对URL
        wsUrl = window.location.protocol === 'https:' ? 'wss:' + wsUrl : 'ws:' + wsUrl;
      } else if (wsUrl.startsWith('http://')) {
        wsUrl = wsUrl.replace('http://', 'ws://');
      } else if (wsUrl.startsWith('https://')) {
        wsUrl = wsUrl.replace('https://', 'wss://');
      }
      wsUrl = wsUrl + 'ws/chat';

      this.ws = createWebSocket({
        url: wsUrl,
        token: this.chatToken,
        onOpen: () => {
          console.log('聊天 WebSocket 已连接')
          this.wsConnected = true
        },
        onMessage: (data) => {
          this.handleWebSocketMessage(data)
        },
        onClose: () => {
          console.log('聊天 WebSocket 已断开')
          this.wsConnected = false
        },
        onError: (error) => {
          console.error('WebSocket 错误:', error)
          this.wsConnected = false
        },
        onReconnect: (attempt) => {
          console.log('正在重连...', attempt)
        }
      })

      // 连接 WebSocket
      this.ws.connect(wsUrl, this.chatToken)
    },

    // 处理 WebSocket 消息
    handleWebSocketMessage(data) {
      console.log('收到 WebSocket 消息:', data)

      switch (data.type) {
        case 'CONNECTED':
          console.log('WebSocket 连接成功，用户ID:', data.senderId)
          break

        case 'JOIN':
          this.handleUserJoin(data)
          break

        case 'LEAVE':
          this.handleUserLeave(data)
          break

        case 'CHAT':
          this.handleChatMessage(data)
          break

        case 'ONLINE':
          this.handleOnlineUsers(data)
          break

        default:
          console.log('未知消息类型:', data.type)
      }
    },

    // 处理用户加入房间
    handleUserJoin(data) {
      console.log('用户加入房间:', data.senderName, '房间ID:', data.roomId)
      if (data.data && this.currentRoom && this.currentRoom.id == data.roomId) {
        this.currentRoomMembers = data.data
      }
    },

    // 处理用户离开房间
    handleUserLeave(data) {
      console.log('用户离开房间:', data.senderName, '房间ID:', data.roomId)
      if (this.currentRoom && this.currentRoom.id == data.roomId) {
        this.loadRoomMembers(data.roomId)
      }
    },

    // 处理聊天消息
    handleChatMessage(data) {
      const roomId = data.roomId
      const message = {
        id: data.data,
        roomId: roomId,
        senderId: data.senderId,
        senderName: data.senderName,
        senderAvatar: data.senderAvatar,
        senderType: data.senderType,
        content: data.content,
        msgType: data.msgType,
        createTime: new Date(data.timestamp).toISOString()
      }

      if (!this.messages[roomId]) {
        this.$set(this.messages, roomId, [])
      }
      this.messages[roomId].push(message)

      if (this.currentRoom && this.currentRoom.id == roomId) {
        this.$nextTick(() => {
          this.scrollToBottom()
        })
      } else {
        const room = this.roomList.find(r => r.id == roomId)
        if (room) {
          room.unReadNum = (room.unReadNum || 0) + 1
          this.calculateTotalUnread()
        }
      }

      this.moveRoomToTop(roomId)
      this.saveLocalData()
    },

    // 处理在线用户列表
    handleOnlineUsers(data) {
      console.log('在线用户列表:', data.data)
    },

    // 加入房间
    joinRoom(roomId) {
      if (!this.ws || !this.wsConnected) {
        console.error('WebSocket 未连接')
        return
      }

      if (this.currentJoinedRoomId && this.currentJoinedRoomId !== roomId) {
        this.leaveRoom(this.currentJoinedRoomId)
      }

      const joinMsg = {
        type: 'JOIN',
        roomId: roomId
      }
      this.ws.send(joinMsg)
      this.currentJoinedRoomId = roomId
      console.log('发送 JOIN 消息，房间ID:', roomId)
    },

    // 离开房间
    leaveRoom(roomId) {
      if (!this.ws || !this.wsConnected) {
        return
      }

      const leaveMsg = {
        type: 'LEAVE',
        roomId: roomId
      }
      this.ws.send(leaveMsg)
      console.log('发送 LEAVE 消息，房间ID:', roomId)
    },

    // 获取聊天室成员列表
    async loadRoomMembers(roomId) {
      const [err, result] = await to(getRoomMemberList({ roomId: roomId }, this.chatToken));
      if (err) {
        console.error('获取成员列表失败:', err)
        return
      }
      if (result.code === 200) {
        this.currentRoomMembers = result.data || []
      }
    },

    // 获取聊天室未读消息
    async loadUnReadMessages(roomId) {
      const [err, result] = await to(getUnReadMessages({ roomId: roomId }, this.chatToken));
      if (err) {
        console.error('获取未读消息失败:', err)
        return
      }
      if (result.code === 200) {
        if (!this.messages[roomId]) {
          this.$set(this.messages, roomId, [])
        }
        // 合并消息，避免重复
        const existingIds = new Set(this.messages[roomId].map(m => m.id))
        const newMessages = (result.data || []).filter(m => !existingIds.has(m.id))
        this.messages[roomId] = [...this.messages[roomId], ...newMessages]
        this.saveLocalData()
      }
    },

    // 标记消息已读
    async markMessageRead(roomId) {
      const [err, result] = await to(readMessage({ roomId: roomId }, this.chatToken));
      if (err) {
        console.error('标记已读失败:', err)
      }
    },

    // 将聊天室移到顶部
    moveRoomToTop(roomId) {
      const index = this.roomList.findIndex(r => r.id === roomId)
      if (index > 0) {
        const room = this.roomList.splice(index, 1)[0]
        this.roomList.unshift(room)
      }
    },

    // 切换聊天框显示
    toggleChat() {
      this.showChat = !this.showChat
      if (this.showChat && this.currentRoom) {
        this.$nextTick(() => {
          this.scrollToBottom()
        })
      }
    },

    // 关闭聊天框
    closeChat() {
      this.showChat = false
      this.hasDragged = false
      this.chatBoxX = 0
      this.chatBoxY = 0
    },

    // 切换 tab
    switchTab(tab) {
      this.activeTab = tab;
      if (tab === 'chat') {
        this.loadRoomList()
      } else if (tab === 'contacts') {
        this.loadTeacherList()
      }
    },

    // 选择聊天室（从最近聊天列表）
    async selectRoom(room) {
      this.currentRoom = room
      await this.loadUnReadMessages(room.id)
      if (room.unReadNum > 0) {
        await this.markMessageRead(room.id)
        room.unReadNum = 0
        this.calculateTotalUnread()
        this.saveLocalData()
      }
      this.joinRoom(room.id)
      this.$nextTick(() => {
        this.scrollToBottom()
      })
    },

    // 选择老师（从联系人列表，创建或获取私聊房间）
    async selectTeacher(teacher) {
      

      // 通过接口获取或创建与该老师的私聊房间
      const [err, result] = await to(getOrCreatePrivateRoom({ targetUserId: teacher.userId }, this.chatToken));
      if (err) {
        console.error('获取私聊房间失败:', err)
        this.$message.error('获取聊天室失败')
        return
      }
      if (result.code === 200 && result.data) {
        const room = result.data
        this.loadRoomList()
       
        // 选择该房间
        await this.selectRoom(room)
        // 切换到聊天tab
        this.activeTab = 'chat';
        
      } else {
        this.$message.error(result.msg || '获取聊天室失败')
      }
    },

    // 发送文本消息
    sendTextMessage() {
      const content = this.messageInput.trim()
      if (!content) return

      if (!this.currentRoom) {
        this.$message.warning('请先选择一个聊天室')
        return
      }

      if (!this.ws || !this.wsConnected) {
        this.$message.warning('聊天服务未连接，请稍后重试')
        return
      }

      const chatMsg = {
        type: 'CHAT',
        roomId: this.currentRoom.id,
        content: content,
        msgType: 1 // 1-文本
      }
      this.ws.send(chatMsg)
      this.messageInput = ''
    },

    // 处理回车键
    handleEnterKey(e) {
      if (e.shiftKey) return
      e.preventDefault()
      this.sendTextMessage()
    },

    // 选择图片
    handleImageSelect(file) {
      const isImage = file.raw.type.startsWith('image/')
      const isLt5M = file.raw.size / 1024 / 1024 < 5

      if (!isImage) {
        this.$message.error('只能上传图片文件')
        return
      }
      if (!isLt5M) {
        this.$message.error('图片大小不能超过 5MB')
        return
      }

      if (!this.currentRoom) {
        this.$message.warning('请先选择一个聊天室')
        return
      }

      if (!this.ws || !this.wsConnected) {
        this.$message.warning('聊天服务未连接，请稍后重试')
        return
      }

      const reader = new FileReader()
      reader.onload = (e) => {
        const chatMsg = {
          type: 'CHAT',
          roomId: this.currentRoom.id,
          content: e.target.result,
          msgType: 2 // 2-图片
        }
        this.ws.send(chatMsg)
      }
      reader.readAsDataURL(file.raw)
    },

    // 选择文件
    handleFileSelect(file) {
      const isLt10M = file.raw.size / 1024 / 1024 < 10

      if (!isLt10M) {
        this.$message.error('文件大小不能超过 10MB')
        return
      }

      if (!this.currentRoom) {
        this.$message.warning('请先选择一个聊天室')
        return
      }

      if (!this.ws || !this.wsConnected) {
        this.$message.warning('聊天服务未连接，请稍后重试')
        return
      }

      const reader = new FileReader()
      reader.onload = (e) => {
        const chatMsg = {
          type: 'CHAT',
          roomId: this.currentRoom.id,
          content: e.target.result,
          msgType: 3 // 3-文件
        }
        this.ws.send(chatMsg)
      }
      reader.readAsDataURL(file.raw)
    },

    // 下载文件
    downloadFile(message) {
      const link = document.createElement('a')
      link.href = message.content
      link.download = message.name || 'file'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },

    // 滚动到底部
    scrollToBottom() {
      const container = this.$refs.messageList
      if (container) {
        container.scrollTop = container.scrollHeight
      }
    },

    // 格式化时间（会话列表用）
    formatTime(timeStr) {
      if (!timeStr) return ''

      const date = new Date(timeStr)
      const now = new Date()
      const diff = now - date

      if (date.toDateString() === now.toDateString()) {
        return this.padZero(date.getHours()) + ':' + this.padZero(date.getMinutes())
      }

      const yesterday = new Date(now)
      yesterday.setDate(yesterday.getDate() - 1)
      if (date.toDateString() === yesterday.toDateString()) {
        return '昨天'
      }

      if (diff < 7 * 24 * 60 * 60 * 1000) {
        const days = ['周日', '周一', '周二', '周三', '周四', '周五', '周六']
        return days[date.getDay()]
      }

      return (date.getMonth() + 1) + '/' + date.getDate()
    },

    // 格式化完整时间（消息时间戳用）
    formatFullTime(timeStr) {
      if (!timeStr) return ''

      const date = new Date(timeStr)
      const now = new Date()

      if (date.toDateString() === now.toDateString()) {
        return this.padZero(date.getHours()) + ':' + this.padZero(date.getMinutes())
      }

      const yesterday = new Date(now)
      yesterday.setDate(yesterday.getDate() - 1)
      if (date.toDateString() === yesterday.toDateString()) {
        return '昨天 ' + this.padZero(date.getHours()) + ':' + this.padZero(date.getMinutes())
      }

      if (date.getFullYear() === now.getFullYear()) {
        return (date.getMonth() + 1) + '月' + date.getDate() + '日 ' +
               this.padZero(date.getHours()) + ':' + this.padZero(date.getMinutes())
      }

      return date.getFullYear() + '年' + (date.getMonth() + 1) + '月' + date.getDate() + '日 ' +
             this.padZero(date.getHours()) + ':' + this.padZero(date.getMinutes())
    },

    // 是否显示时间分割线
    showTimeDivider(message, index) {
      if (index === 0) return true

      const prevMessage = this.currentMessages[index - 1]
      const currentTime = new Date(message.createTime).getTime()
      const prevTime = new Date(prevMessage.createTime).getTime()
      return currentTime - prevTime > 5 * 60 * 1000
    },

    // 补零
    padZero(num) {
      return num < 10 ? '0' + num : num
    },

    // 开始拖拽
    startDrag(e) {
      // 只响应鼠标左键
      if (e.button !== 0) return

      const chatBox = this.$refs.chatBox
      const rect = chatBox.getBoundingClientRect()

      // 设置当前位置（首次拖拽时从当前位置开始）
      this.chatBoxX = rect.left
      this.chatBoxY = rect.top
      this.hasDragged = true

      // 记录鼠标在聊天框内的相对位置
      this.dragStartX = e.clientX - rect.left
      this.dragStartY = e.clientY - rect.top

      this.isDragging = true

      // 添加全局事件监听
      document.addEventListener('mousemove', this.onDrag)
      document.addEventListener('mouseup', this.stopDrag)

      // 防止选中文本
      e.preventDefault()
    },

    // 拖拽中
    onDrag(e) {
      if (!this.isDragging) return

      // 计算新位置
      let newX = e.clientX - this.dragStartX
      let newY = e.clientY - this.dragStartY

      // 获取窗口尺寸和聊天框尺寸
      const chatBox = this.$refs.chatBox
      const boxWidth = chatBox.offsetWidth
      const boxHeight = chatBox.offsetHeight
      const windowWidth = window.innerWidth
      const windowHeight = window.innerHeight

      // 限制在可视区域内
      newX = Math.max(0, Math.min(newX, windowWidth - boxWidth))
      newY = Math.max(0, Math.min(newY, windowHeight - boxHeight))

      this.chatBoxX = newX
      this.chatBoxY = newY
    },

    // 停止拖拽
    stopDrag() {
      this.isDragging = false
      document.removeEventListener('mousemove', this.onDrag)
      document.removeEventListener('mouseup', this.stopDrag)
    }
  }
}
</script>

<style lang="scss" scoped>
// 微信风格配色
$wechat-green: #2f78ff;
$wechat-green-light: #99BBFA;
$sidebar-bg: #2E2E2E;
$sidebar-hover: #3A3A3A;
$sidebar-active: #3A3A3A;
$chat-bg: #FFFFFF;
$border-color: #E8E8E8;
$text-white: #FFFFFF;
$text-gray: #B2B2B2;
$text-dark: #1A1A1A;
$text-secondary: #888888;

.customer_service_wrap {
  position: fixed;
  right: 30px;
  bottom: 80px;
  z-index: 99999999;
}

// 客服按钮
.service_btn {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, $wechat-green 0%, $wechat-green-light 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba($wechat-green, 0.4);
  transition: all 0.3s;
  position: relative;

  i {
    font-size: 28px;
    color: #fff;
  }

  &:hover {
    transform: scale(1.08);
    box-shadow: 0 6px 20px rgba($wechat-green, 0.5);
  }

  .unread_dot {
    position: absolute;
    top: -2px;
    right: -2px;
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    background: #FA5151;
    color: #fff;
    font-size: 11px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 500;
  }
}

// 聊天框
.chat_box {
  position: absolute;
  right: 0;
  bottom: 76px;
  width: 760px;
  height: 560px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.2);
  display: flex;
  overflow: hidden;

  // 最左侧导航栏
  .nav_bar {
    width: 54px;
    background: #1F1F1F;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-top: 16px;
    flex-shrink: 0;

    .nav_item {
      width: 40px;
      height: 40px;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      margin-bottom: 8px;
      position: relative;
      transition: all 0.2s;

      i {
        font-size: 22px;
        color: $text-gray;
        transition: color 0.2s;
      }

      &:hover {
        background: rgba(255, 255, 255, 0.1);

        i {
          color: $text-white;
        }
      }

      &.active {
        background: $wechat-green;

        i {
          color: $text-white;
        }
      }

      .nav_badge {
        position: absolute;
        top: -2px;
        right: -2px;
        min-width: 16px;
        height: 16px;
        padding: 0 4px;
        background: #FA5151;
        color: #fff;
        font-size: 10px;
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }
  }

  // 左侧会话列表
  .chat_sidebar {
    width: 220px;
    background: $sidebar-bg;
    display: flex;
    flex-direction: column;
    flex-shrink: 0;

    .sidebar_header {
      height: 50px;
      padding: 0 16px;
      display: flex;
      align-items: center;
      border-bottom: 1px solid rgba(255, 255, 255, 0.06);

      .sidebar_title {
        font-size: 14px;
        color: $text-white;
        font-weight: 500;
      }
    }

    .conversation_list {
      flex: 1;
      overflow-y: auto;

      &::-webkit-scrollbar {
        width: 4px;
      }

      &::-webkit-scrollbar-thumb {
        background: rgba(255, 255, 255, 0.2);
        border-radius: 2px;
      }

      .conversation_item {
        display: flex;
        align-items: center;
        padding: 12px 12px;
        cursor: pointer;
        transition: background 0.2s;

        &:hover {
          background: $sidebar-hover;
        }

        &.active {
          background: $sidebar-active;
        }

        .conv_avatar {
          width: 40px;
          height: 40px;
          border-radius: 4px;
          background: linear-gradient(135deg, $wechat-green 0%, $wechat-green-light 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          position: relative;
          overflow: hidden;

          img {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }

          .avatar_text {
            color: #fff;
            font-size: 16px;
            font-weight: 500;
          }

          .conv_unread {
            position: absolute;
            top: -6px;
            right: -6px;
            min-width: 16px;
            height: 16px;
            padding: 0 4px;
            background: #FA5151;
            color: #fff;
            font-size: 10px;
            border-radius: 8px;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          &.group_avatar {
            border-radius: 8px;
            background: linear-gradient(135deg, rgb(3, 78, 158) 0%, #63abee 100%);

            i {
              font-size: 22px;
              color: #fff;
            }
          }
        }

        .conv_info {
          flex: 1;
          margin-left: 10px;
          min-width: 0;

          .conv_top {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 4px;

            .conv_name {
              font-size: 14px;
              color: $text-white;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            }

            .conv_time {
              font-size: 11px;
              color: $text-gray;
              flex-shrink: 0;
              margin-left: 8px;
            }
          }

          .conv_bottom {
            .conv_msg {
              font-size: 12px;
              color: $text-gray;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
              display: block;
            }
          }
        }
      }

      .empty_list {
        text-align: center;
        padding: 40px 0;
        color: $text-gray;
        font-size: 13px;
      }
    }

    // 文件 tabs
    .file_tabs {
      display: flex;
      flex-direction: column;
      padding: 0;

      .file_tab {
        padding: 14px 16px;
        display: flex;
        align-items: center;
        gap: 10px;
        cursor: pointer;
        color: $text-gray;
        font-size: 13px;
        transition: all 0.2s;
        border-left: 3px solid transparent;

        i {
          font-size: 18px;
        }

        &:hover {
          background: $sidebar-hover;
          color: $text-white;
        }

        &.active {
          color: $wechat-green;
          background: $sidebar-active;
          border-left-color: $wechat-green;
        }
      }
    }

    // 文件列表
    .file_list {
      flex: 1;
      overflow-y: auto;
      padding: 12px;

      &::-webkit-scrollbar {
        width: 4px;
      }

      &::-webkit-scrollbar-thumb {
        background: rgba(255, 255, 255, 0.2);
        border-radius: 2px;
      }

      .image_grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 8px;

        .image_item {
          aspect-ratio: 1;
          border-radius: 4px;
          overflow: hidden;
          position: relative;
          cursor: pointer;

          .el-image {
            width: 100%;
            height: 100%;
          }

          .image_info {
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            padding: 4px;
            background: linear-gradient(transparent, rgba(0, 0, 0, 0.6));
            color: #fff;
            font-size: 10px;
            text-align: center;
          }
        }
      }

      .file_item {
        display: flex;
        align-items: center;
        padding: 10px;
        border-radius: 4px;
        cursor: pointer;
        transition: background 0.2s;

        &:hover {
          background: rgba(255, 255, 255, 0.05);
        }

        > i {
          font-size: 32px;
          color: $wechat-green;
          margin-right: 10px;
        }

        .file_info {
          flex: 1;
          min-width: 0;

          .file_name {
            display: block;
            font-size: 13px;
            color: $text-white;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            margin-bottom: 4px;
          }

          .file_date {
            font-size: 11px;
            color: $text-gray;
          }
        }
      }
    }
  }

  // 右侧聊天区域
  .chat_main {
    flex: 1;
    display: flex;
    flex-direction: column;
    background: $chat-bg;
    min-width: 0;

    .chat_header {
      height: 50px;
      padding: 0 16px;
      background: $chat-bg;
      border-bottom: 1px solid $border-color;
      display: flex;
      align-items: center;
      justify-content: space-between;

      &.drag_handle {
        cursor: move;
        user-select: none;
      }

      .chat_title {
        font-size: 15px;
        color: $text-dark;
        font-weight: 500;
      }

      .member_count {
        font-size: 13px;
        color: $text-secondary;
        margin-left: 6px;
      }

      &.empty_header {
        justify-content: flex-end;
      }

      .header_actions {
        display: flex;
        gap: 16px;

        i {
          font-size: 16px;
          color: $text-secondary;
          cursor: pointer;
          transition: color 0.2s;

          &:hover {
            color: $text-dark;
          }
        }
      }
    }

    .message_list {
      flex: 1;
      overflow-y: auto;
      padding: 16px 20px;
      background: $chat-bg;

      &::-webkit-scrollbar {
        width: 6px;
      }

      &::-webkit-scrollbar-thumb {
        background: #D0D0D0;
        border-radius: 3px;
      }

      .message_wrapper {
        margin-bottom: 16px;
      }

      .time_divider {
        text-align: center;
        margin: 20px 0;

        span {
          display: inline-block;
          background: #DADADA;
          padding: 4px 10px;
          border-radius: 3px;
          font-size: 11px;
          color: #fff;
        }
      }

      .message_item {
        display: flex;
        align-items: flex-start;

        .message_avatar {
          width: 36px;
          height: 36px;
          border-radius: 4px;
          background: linear-gradient(135deg, $wechat-green 0%, $wechat-green-light 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          overflow: hidden;

          img {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }

          .avatar_text {
            color: #fff;
            font-size: 14px;
            font-weight: 500;
          }
        }

        .message_body {
          margin-left: 10px;
          max-width: 65%;

          .sender_name {
            font-size: 12px;
            color: $text-secondary;
            margin-bottom: 4px;
            margin-left: 4px;
          }

          .message_bubble {
            background: #F5F5F5;
            padding: 10px 14px;
            border-radius: 4px;
            font-size: 14px;
            line-height: 1.5;
            color: $text-dark;
            word-break: break-word;
            position: relative;

            // 左侧小尖角
            &::before {
              content: '';
              position: absolute;
              left: -8px;
              top: 12px;
              border: 4px solid transparent;
              border-right-color: #F5F5F5;
            }

            &.image_bubble {
              padding: 4px;
              background: transparent;

              &::before {
                display: none;
              }
            }

            &.file_bubble {
              padding: 0;
              background: #fff;
              border: 1px solid $border-color;

              &::before {
                display: none;
              }
            }

            .message_text {
              white-space: pre-wrap;
            }

            .message_image {
              max-width: 200px;
              max-height: 200px;
              border-radius: 4px;
              cursor: pointer;
              display: block;
            }

            .message_file {
              display: flex;
              align-items: center;
              padding: 12px 16px;
              min-width: 200px;
              cursor: pointer;
              transition: background 0.2s;

              &:hover {
                background: #F5F5F5;
              }

              > i {
                font-size: 36px;
                color: $wechat-green;
                margin-right: 12px;
              }

              .file_info {
                flex: 1;
                min-width: 0;

                .file_name {
                  display: block;
                  font-size: 14px;
                  color: $text-dark;
                  overflow: hidden;
                  text-overflow: ellipsis;
                  white-space: nowrap;
                  max-width: 150px;
                  margin-bottom: 4px;
                }

                .file_size {
                  font-size: 12px;
                  color: $text-secondary;
                }
              }
            }
          }

          .message_status {
            margin-left: 8px;
            font-size: 14px;

            .el-icon-loading {
              color: #999;
            }

            .el-icon-warning {
              color: #FA5151;
              cursor: pointer;
            }
          }
        }

        // 自己发的消息
        &.message_self {
          flex-direction: row-reverse;

          .message_body {
            margin-left: 0;
            margin-right: 10px;
            flex-direction: row-reverse;

            .message_bubble {
              background: $wechat-green-light;
              color: $text-dark;

              // 右侧小尖角
              &::before {
                left: auto;
                right: -8px;
                border-right-color: transparent;
                border-left-color: $wechat-green-light;
              }
            }

            .message_status {
              margin-left: 0;
              margin-right: 8px;
            }
          }
        }
      }

      .empty_message {
        text-align: center;
        color: #999;
        padding: 80px 0;
        font-size: 13px;
      }
    }

    .input_area {
      background: $chat-bg;
      border-top: 1px solid $border-color;

      .input_box {
        background: #fff;
        display: flex;
        flex-direction: column;

        .input_toolbar {
          padding: 8px 12px;
          display: flex;
          align-items: center;
          gap: 8px;

          .toolbar_icon {
            font-size: 20px;
            color: #666;
            cursor: pointer;
            transition: color 0.2s;
            padding: 4px;

            &:hover {
              color: $text-dark;
            }
          }
        }

        .input_container {
          textarea {
            width: 100%;
            height: 80px;
            border: none;
            outline: none;
            padding: 10px 12px;
            font-size: 14px;
            line-height: 1.5;
            resize: none;
            font-family: inherit;
            color: $text-dark;

            &::placeholder {
              color: #C0C0C0;
            }
          }
        }
      }

    }

    .chat_placeholder {
      flex: 1;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      color: #999;
      background: $chat-bg;

      i {
        font-size: 60px;
        color: #D0D0D0;
        margin-bottom: 16px;
      }

      p {
        font-size: 14px;
      }
    }

    // 文件记录主区域
    .files_main_content {
      flex: 1;
      display: flex;
      flex-direction: column;
      overflow: hidden;

      .files_tab_bar {
        display: flex;
        padding: 12px 20px;
        border-bottom: 1px solid $border-color;
        gap: 24px;

        .files_tab_item {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 16px;
          cursor: pointer;
          color: $text-secondary;
          font-size: 14px;
          border-radius: 4px;
          transition: all 0.2s;

          i {
            font-size: 18px;
          }

          &:hover {
            background: #F5F5F5;
            color: $text-dark;
          }

          &.active {
            background: rgba($wechat-green, 0.1);
            color: $wechat-green;
          }
        }
      }

      .files_display_area {
        flex: 1;
        overflow-y: auto;
        padding: 16px 20px;

        &::-webkit-scrollbar {
          width: 6px;
        }

        &::-webkit-scrollbar-thumb {
          background: #D0D0D0;
          border-radius: 3px;
        }

        // 图片画廊
        .images_gallery {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 12px;

          .gallery_item {
            border-radius: 8px;
            overflow: hidden;
            background: #F5F5F5;
            cursor: pointer;
            transition: transform 0.2s;

            &:hover {
              transform: scale(1.02);
            }

            .el-image {
              width: 100%;
              aspect-ratio: 1;
              display: block;
            }

            .gallery_item_info {
              padding: 8px 10px;
              background: #fff;
              border-top: 1px solid $border-color;

              .sender {
                display: block;
                font-size: 12px;
                color: $text-dark;
                margin-bottom: 2px;
              }

              .time {
                font-size: 11px;
                color: $text-secondary;
              }
            }
          }
        }

        // 文件列表
        .files_list_main {
          .file_list_item {
            display: flex;
            align-items: center;
            padding: 12px 16px;
            border-radius: 8px;
            margin-bottom: 8px;
            background: #F9F9F9;
            cursor: pointer;
            transition: background 0.2s;

            &:hover {
              background: #F0F0F0;
            }

            > i {
              font-size: 36px;
              color: $wechat-green;
              margin-right: 12px;
            }

            .file_detail {
              flex: 1;

              .file_name {
                display: block;
                font-size: 14px;
                color: $text-dark;
                margin-bottom: 4px;
              }

              .file_meta {
                font-size: 12px;
                color: $text-secondary;

                span {
                  margin-right: 12px;
                }
              }
            }
          }
        }

        // 空状态
        .files_empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 300px;
          color: $text-secondary;

          i {
            font-size: 64px;
            color: #D0D0D0;
            margin-bottom: 16px;
          }

          p {
            font-size: 14px;
          }
        }
      }
    }
  }
}

// 动画
.chat-slide-enter-active,
.chat-slide-leave-active {
  transition: all 0.3s ease;
}

.chat-slide-enter,
.chat-slide-leave-to {
  opacity: 0;
  transform: translateY(20px) scale(0.95);
}
</style>



// WEBPACK FOOTER //
// src/components/CustomerServiceChat.vue